import React from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import * as http from '../../http';
import { useAppDispatch } from 'hooks';
import { CDBurnerFilter, FilterCondition, FilterConditionKey } from 'reducers/burnerFiltersReducer';
import * as cdBurnfiltersActions from '../../actions/burnerFiltersActions';

const newFilter: CDBurnerFilter = {
  pk: 0,
  enabled: false,
  filterTag: '',
  filterCondition: 'STARTS_WITH' as FilterConditionKey,
  filterValue: '',
  notes: '',
};

type TAutoPrintRuleModal = {
  editMode: boolean;
  filter?: CDBurnerFilter;
  show: boolean;
  handleClose: () => void;
};

const CDBurnerFilterModal = ({ editMode, filter, show, handleClose }: TAutoPrintRuleModal) => {
  const editFilter = filter === undefined ? newFilter : filter;
  const dispatch = useAppDispatch();

  const toaster = {
    success: message => dispatch({ type: 'SUCCESS', message }),
    info: message => dispatch({ type: 'INFO', message }),
    warning: message => dispatch({ type: 'WARNING', message }),
    error: message => dispatch({ type: 'ERROR', message }),
  };

  const schema = yup.object().shape({
    filterTag: yup
      .string()
      .test('test-node', 'The tag to check cannot be empty', (val: string) => {
        if (val && val.length > 0) {
          return true;
        }
        return false;
      })
      .required('This field is required'),
    enabled: yup.boolean(),
    filterCondition: yup.string(),
    filterValue: yup.string(),
    notes: yup.string(),
  });

  async function submitFilter(values: CDBurnerFilter): Promise<boolean> {
    let responseStatus = false;
    if (editMode) {
      await http
        .put(
          `/rest/react/cd-burner-filters`,
          {
            pk: values.pk,
            enabled: values.enabled,
            filterTag: values.filterTag,
            filterCondition: values.filterCondition,
            filterValue: values.filterValue,
            notes: values.notes,
          }
        )
        .then(res => {
          dispatch(cdBurnfiltersActions.getFilters());
          toaster.success(`CD burn filter successfully edited`);
          responseStatus = true;
        })
        .catch(e => toaster.error(e.message));
    } else {
      await http
        .post(
          `/rest/react/cd-burner-filters`,
          {
            enabled: values.enabled,
            filterTag: values.filterTag,
            filterCondition: values.filterCondition,
            filterValue: values.filterValue,
            notes: values.notes,
          }
        )
        .then(res => {
          dispatch(cdBurnfiltersActions.getFilters());
          toaster.success('CD burn filter added successfully');
          responseStatus = true;
        })
        .catch(e => toaster.error(e.message));
    }
    return responseStatus;
  }

  return (
    <React.Fragment>
      <Modal show={show} size="lg" onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>{editMode ? <span>Editing CD burner filter</span> : <span>New CD burner filter</span>}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            validateOnChange={true}
            validationSchema={schema}
            onSubmit={async (values, actions) => {
              const success: boolean = await submitFilter(values);
              actions.setSubmitting(false);
              if (success) {
                handleClose();
              }
            }}
            validateOnMount={editMode}
            initialValues={editFilter}
            initialTouched={{
              filterTag: editMode,
              filterCondition: editMode,
              filterValue: editMode,
              notes: editMode,
            }}
          >
            {({ handleSubmit, handleChange, setFieldValue, values, touched, errors }) => (
              <Form id="rule-form" onSubmit={handleSubmit} encType="multipart/form-data">
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Tag
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      size="sm"
                      defaultValue={values.filterTag}
                      onChange={handleChange}
                      name="filterTag"
                      isInvalid={touched.filterTag && errors.filterTag !== undefined}
                    />
                    <Form.Control.Feedback type="invalid">{errors.filterTag}</Form.Control.Feedback>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Condition
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      as="select"
                      size="sm"
                      onChange={handleChange}
                      defaultValue={values.filterCondition}
                      name="filterCondition"
                    >
                      {Object.entries(FilterCondition).map(([enumKey, enumValue]) => (
                        <option key={'cd-burn-condition-' + enumKey} value={enumKey}>
                          {enumValue}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Value
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      type="text"
                      size="sm"
                      onChange={handleChange}
                      defaultValue={values.filterValue}
                      name="filterValue"
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Notes
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control type="text" size="sm" onChange={handleChange} value={values.notes} name="notes" />
                  </Col>
                </Form.Group>

                <Form.Group as={Row}>
                  <Form.Label as={Col} sm={4} md={{ offset: 1 }}>
                    Enabled
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Check
                      custom
                      id="enabled-filter-check"
                      label=""
                      type="checkbox"
                      onChange={e => setFieldValue('enabled', e.target.checked)}
                      checked={values.enabled}
                      name="enabled"
                    />
                  </Col>
                </Form.Group>
              </Form>
            )}
          </Formik>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          {editMode ? (
            <Button variant="primary" type="submit" form="rule-form">
              Submit edit
            </Button>
          ) : (
            <Button variant="primary" type="submit" form="rule-form">
              Add filter
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default CDBurnerFilterModal;
